/**
 * The shapes a date or a time is stored in, and keeping an edited value in the shape it had.
 *
 * Nothing on the server knows what a date is. A row is JSON, stored as sent and handed back
 * as stored, so a value comes back in exactly the shape somebody wrote it in — and the
 * trouble is that several somebodies write the same column. The app picks a shape when it is
 * generated (`day: '2026-09-15'`, `startsAt: '18:00'`, joined later as `day + 'T' + startsAt`),
 * and then the owner edits the rows by hand, and whatever they type is what the app reads next.
 *
 * The cafe rota is what that costs. Its shifts hold `day` and `startsAt` as full timestamps
 * (`2026-09-12T01:00:00.000Z`) while the app joins them as a date and a clock, so every join
 * produced `2026-09-12T01:00:00.000ZT2026-09-12T01:00:00.000Z` — not a date — and the render
 * had to be rescued by a runtime that guesses which half was meant.
 *
 * So the rule is the column's, not the typist's: an edit keeps the shape the value already
 * had. These are the four shapes, which are also exactly what the browser's own inputs
 * produce (`<input type="date">`, `type="time"`, `type="datetime-local"`) plus the ISO
 * instant `toISOString()` writes:
 *
 *   date     2026-09-15                 a calendar day, the same day wherever you read it
 *   time     18:30                      a clock time, no day attached
 *   local    2026-09-15T18:30           a day and a clock, no zone — the reader's own time
 *   instant  2026-09-16T01:30:00.000Z   one moment, the same everywhere
 *
 * Pure: no React, no network, so both editors and their tests share one definition.
 */
export type DateShape = 'date' | 'time' | 'local' | 'instant';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})([T ])(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;
const INSTANT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Which of the four shapes a stored value is in, or null when it is not a date or time. */
export function dateShapeOf(value: unknown): DateShape | null {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (DATE_RE.test(s)) return 'date';
  if (TIME_RE.test(s)) return 'time';
  if (LOCAL_RE.test(s)) return 'local';
  if (INSTANT_RE.test(s) && !Number.isNaN(Date.parse(s))) return 'instant';
  return null;
}

/** What a shape is called, and an example of it, in the words shown to the owner. */
export const DATE_SHAPE_WORDS: Record<DateShape, { what: string; example: string }> = {
  date: { what: 'a date', example: '2026-09-15' },
  time: { what: 'a time', example: '18:30' },
  local: { what: 'a date and time', example: '2026-09-15 18:30' },
  instant: { what: 'a date and time', example: '2026-09-15 18:30' },
};

interface DayParts { y: number; mo: number; d: number }
interface ClockParts { h: number; mi: number; s: number }
type Parts = Partial<DayParts & ClockParts> & { at?: Date };

const pad = (n: number) => String(n).padStart(2, '0');

function validDay(y: number, mo: number, d: number): boolean {
  return mo >= 1 && mo <= 12 && d >= 1 && d <= new Date(y, mo, 0).getDate();
}

function localParts(at: Date): DayParts & ClockParts {
  return {
    y: at.getFullYear(), mo: at.getMonth() + 1, d: at.getDate(),
    h: at.getHours(), mi: at.getMinutes(), s: at.getSeconds(),
  };
}

/** `18:30`, `9:05`, `18:30:15`, `6pm`, `6:30 pm`, `6.30am` is not accepted — a dot is a number. */
function readClock(text: string): ClockParts | null {
  const t = text.trim();
  const h24 = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/.exec(t);
  if (h24) {
    const h = Number(h24[1]);
    const mi = Number(h24[2]);
    const s = Number(h24[3] ?? 0);
    return h <= 23 && mi <= 59 && s <= 59 ? { h, mi, s } : null;
  }
  const h12 = /^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s*m\.?$/i.exec(t);
  if (h12) {
    const raw = Number(h12[1]);
    const mi = Number(h12[2] ?? 0);
    if (raw < 1 || raw > 12 || mi > 59) return null;
    return { h: (raw % 12) + (/p/i.test(h12[3]) ? 12 : 0), mi, s: 0 };
  }
  return null;
}

/**
 * What a person typed: a day, a clock time, both, or a whole timestamp.
 *
 * Deliberately narrow. `09/10/2026` is the ninth of October to half the world and the tenth
 * of September to the other half, and a guess about which is a shift on the wrong day with
 * nothing to say so. Anything outside these forms is refused and the owner is shown the form
 * that works.
 */
function readTyped(text: string): Parts | null {
  const s = text.trim();
  if (!s) return null;
  // A timestamp that names its zone is one moment; its parts are read in this device's time,
  // which is the time the owner reads and types in.
  if (INSTANT_RE.test(s)) {
    const at = new Date(s);
    return Number.isNaN(at.getTime()) ? null : { ...localParts(at), at };
  }
  const dated = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:(?:T|\s+)(.+))?$/i.exec(s);
  if (dated) {
    const y = Number(dated[1]);
    const mo = Number(dated[2]);
    const d = Number(dated[3]);
    if (!validDay(y, mo, d)) return null;
    if (dated[4] === undefined) return { y, mo, d };
    const clock = readClock(dated[4]);
    return clock ? { y, mo, d, ...clock } : null;
  }
  return readClock(s);
}

/**
 * An instant as the owner reads it: their own day and clock, `2026-09-15 18:30`.
 *
 * Shown raw, `2026-09-16T01:30:00.000Z` is the right moment in a form nobody reads — a 6:30pm
 * shift in California displayed as "01:30" the next day. The editor shows this instead and
 * `reshapeDate` turns it back into an instant, so the column still holds instants.
 */
export function showInstant(value: string): string {
  const at = new Date(value.trim());
  if (Number.isNaN(at.getTime())) return value;
  const p = localParts(at);
  const clock = `${pad(p.h)}:${pad(p.mi)}${p.s ? `:${pad(p.s)}` : ''}`;
  return `${p.y}-${pad(p.mo)}-${pad(p.d)} ${clock}`;
}

/**
 * `text`, written in the shape `like` is in — or null when `text` cannot be read as one.
 *
 * `like` is a value already in the column: the one being edited, or for a new row, one of its
 * neighbours. It decides the shape, and the small things inside it — `T` or a space, seconds
 * or none — so a column stays uniform and a string sort across it stays a time sort.
 *
 * `fill` is the value being REPLACED, for the part the owner did not retype: moving a
 * `2026-09-15 18:30` session to the 16th by typing `2026-09-16` keeps it at 18:30. A new row
 * has nothing to replace, so a missing part there is refused rather than invented.
 */
export function reshapeDate(text: string, like: string, fill?: string): string | null {
  const shape = dateShapeOf(like);
  if (!shape) return null;
  const typed = readTyped(text);
  if (!typed) return null;
  const base = fill !== undefined ? readTyped(fill) : null;

  const hasDay = typed.y !== undefined;
  const hasClock = typed.h !== undefined;
  const likeTrim = like.trim();

  if (shape === 'date') {
    return hasDay ? `${typed.y}-${pad(typed.mo!)}-${pad(typed.d!)}` : null;
  }

  if (shape === 'time') {
    if (!hasClock) return null;
    const withSeconds = TIME_RE.exec(likeTrim)?.[3] !== undefined;
    return `${pad(typed.h!)}:${pad(typed.mi!)}${withSeconds ? `:${pad(typed.s ?? 0)}` : ''}`;
  }

  // local and instant both need a day AND a clock.
  const day = hasDay ? typed : base?.y !== undefined ? base : null;
  const clock = hasClock ? typed : base?.h !== undefined ? base : null;
  if (!day || !clock) return null;

  if (shape === 'local') {
    const m = LOCAL_RE.exec(likeTrim)!;
    const sep = m[4];
    const withSeconds = m[7] !== undefined;
    return `${day.y}-${pad(day.mo!)}-${pad(day.d!)}${sep}${pad(clock.h!)}:${pad(clock.mi!)}` +
      (withSeconds ? `:${pad(clock.s ?? 0)}` : '');
  }

  // instant: the moment the owner means in their own time, written the way toISOString does.
  const at = typed.at && hasDay && hasClock
    ? typed.at
    : new Date(day.y!, day.mo! - 1, day.d!, clock.h!, clock.mi!, clock.s ?? 0, 0);
  let iso = at.toISOString();
  // Match the column's precision, so `…T01:00:00Z` beside `…T02:00:00Z` does not become a mix.
  if (!/\.\d+/.test(likeTrim) && at.getUTCMilliseconds() === 0) iso = iso.replace(/\.\d{3}Z$/, 'Z');
  if (!/T\d{2}:\d{2}:\d{2}/.test(likeTrim) && at.getUTCSeconds() === 0 && at.getUTCMilliseconds() === 0) {
    iso = iso.replace(/:00(\.000)?Z$/, 'Z');
  }
  return iso;
}
